import { Injectable } from '@angular/core';
import { DatePicker, DateRange } from '@com-delta-omnichannel/mach-core-js';
import { FlightDate } from '../../models/interfaces/flight-status-types.interface';

type DateUpdateCallback = (date: {
  start: Date | null;
  end: Date | null;
  total: number;
}) => void;

@Injectable()
export class FlightStatusService {
  public datePickerInstance?: DatePicker;

  private static setDateRange(
    dates: FlightDate,
    rangeDatePickerInstance: DatePicker
  ): void {
    if (!dates.end)
      rangeDatePickerInstance.selectRange(dates.start, dates.start);
  }

  private static isDateStartNull(dates: DateRange): boolean {
    return !!dates && dates.start === null;
  }

  private static getDateRangeEnd(): Date {
    const today = new Date();
    return new Date(
      today.getFullYear() + 1,
      today.getMonth() + 1,
      today.getDate() - 1
    );
  }

  private static getYesterday(): Date {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }

  private static formatSelectedDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  static handleDateChange(
    rangeDatePickerInstance: DatePicker,
    updateFlightDate: DateUpdateCallback,
    setSelectedDateText: (text: string) => void
  ): void {
    const dates = rangeDatePickerInstance.getSelectedDates();

    if (dates?.start) {
      this.setDateRange(dates as FlightDate, rangeDatePickerInstance);
      rangeDatePickerInstance.close();
      setSelectedDateText(this.formatSelectedDate(dates.start));
      updateFlightDate({ start: dates.start, end: dates.start, total: 1 });
    } else if (this.isDateStartNull(dates)) {
      updateFlightDate({ start: dates.start, end: dates.start, total: 1 });
      setSelectedDateText('');
    }
  }

  static initializeDatePicker(
    rangeDatePickerInstance: DatePicker | null,
    updateFlightDate: DateUpdateCallback,
    setSelectedDateText: (text: string) => void
  ): DatePicker {
    if (!rangeDatePickerInstance) {
      rangeDatePickerInstance = new DatePicker({
        id: 'flight-status-date-picker',
        title: 'Select Departure Date',
        selectionType: 'range' as const,
        triggerElementId: 'flight-status-date-trigger',
        dates: {
          dateRange: {
            start: this.getYesterday(),
            end: this.getDateRangeEnd()
          }
        },
        footer: { primaryButtonLabel: 'Done' }
      });

      rangeDatePickerInstance.onChange(() => {
        if (rangeDatePickerInstance) {
          this.handleDateChange(
            rangeDatePickerInstance,
            updateFlightDate,
            setSelectedDateText
          );
        }
      });
      rangeDatePickerInstance.onClose(() => {
        document.getElementById('flight-status-date-trigger')?.focus();
      });
    }
    return rangeDatePickerInstance;
  }

  openDatePicker(
    rangeDatePickerInstance: DatePicker | null,
    updateFlightDate: DateUpdateCallback,
    setSelectedDateText: (text: string) => void
  ): DatePicker {
    const instance = FlightStatusService.initializeDatePicker(
      rangeDatePickerInstance,
      updateFlightDate,
      setSelectedDateText
    );
    instance.open();
    this.datePickerInstance = instance;
    return instance;
  }

  formatDateForDisplay(date: Date): string {
    if (!date || isNaN(date.getTime())) return 'Select date';

    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateForForm(date: Date): string {
    if (!date || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  destroy(): void {
    this.datePickerInstance?.destroy();
  }
}
